import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Form, Row, Col, Spinner } from "react-bootstrap";
import useMediaQuery from "../../hooks/useMediaQuery";
import CustomCalendar from "../../components/CustomCalendar/CustomCalendar";
import moment from "moment";
import 'moment/locale/it';
import CustomModal from "../../components/CustomModal/CustomModal";
import ComebackArrow from "../../components/ComebackArrow/ComebackArrow";
import { alertService } from '../../services/AlertService';
import useLezioni from "../../hooks/useLezioni";
import useArgomenti from '../../hooks/useArgomenti';
import CustomSearch from '../../components/CustomSearch/CustomSearch';
import useContenutiCorso from '../../hooks/useContenutiCorso';
import UtilityService from "../../services/UtillityServices/UtilityService";
import { TIPO_CORSO } from "../../model/tipoCorso";

const MODAL_TYPES = {
  NEW: 'NEW',
  EDIT: 'EDIT',
  VIEW: 'VIEW',
  DELETE: 'DELETE'
};

export default function LezioniConfigPage() {
  const {
    getLezioniByIdClasse,
    createLezione,
    deleteLezione,
    updateLezione,
    loadingStateLezioni,
  } = useLezioni();

  const { getArgomentiByParams } = useArgomenti();

  const { findContenutiByParams } = useContenutiCorso();

  const location = useLocation();
  const navigate = useNavigate();
  const classe = location.state?.courseData;
  const isWideScreen = useMediaQuery("(min-width: 1200px)");
  const [lessons, setLessons] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newLesson, setNewLesson] = useState({
    titolo: "",
    data: "",
    ora_inizio: "",
    ora_fine: "",
    argomenti: [],
    contenuti: [],
    note: "",
    tipo_lezione: TIPO_CORSO.TELEMATICA_SINCRONA
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [modalMode, setModalMode] = useState(MODAL_TYPES.NEW);

  useEffect(() => {
    if(!classe?.corso?.id_corso) {
      navigate("/gestione-classi")
      return
    }
    fetchLessons();
  }, [classe]);

  const fetchLessons = async () => {
    if (classe?.id_classe) {
      const obj = { id_classe: classe.id_classe }
      const fetchedLessons = await getLezioniByIdClasse(obj);
        if (Array.isArray(fetchedLessons)) {
          const formattedLessons = fetchedLessons.map(lesson => ({
            id_lezione: lesson.id_lezione,
            titolo: lesson.titolo,
            data: lesson.data?.split(' ')[0],
            ora_inizio: lesson.ora_inizio,
            ora_fine: lesson.ora_fine,
            argomenti: lesson.argomenti || [],
            contenuti: lesson.contenuti || [],
            note: lesson.note,
            tipo_lezione: lesson.tipo_lezione
          }));
          setLessons(formattedLessons);
        } else {
          setLessons([]);
        }
    }
  };

  const minDate = classe?.data_inizio ? moment(classe.data_inizio, "YYYY-MM-DD HH:mm:ss").toDate() : new Date();
  const maxDate = classe?.data_fine ? moment(classe.data_fine, "YYYY-MM-DD HH:mm:ss").toDate() : new Date();

  useEffect(() => {
    moment.locale('it'); 
  }, []);

  const formatDate = (date) => {
    if (!date) return '';
    const momentDate = moment(date);
    return momentDate.format('dddd D MMMM YYYY').replace(/^\w/, (c) => c.toUpperCase());
  };

  const handleSelectSlot = (slotInfo) => {
    const data = moment(slotInfo.start).format("YYYY-MM-DD");
    const ora_inizio = moment(slotInfo.start).format("HH:mm");
    const ora_fine = moment(slotInfo.end).format("HH:mm");
/*     if (lessons.some(lesson => lesson.data === data)) {
      alertService.warn("Esiste già una lezione per questa data", {autoClose: true});
      return;
    } */
    setNewLesson({ 
      titolo: "",
      data,
      ora_inizio,
      ora_fine,
      argomenti: [],
      contenuti: [],
      note: "",
      tipo_lezione: classe?.corso?.modalita_erogazione === TIPO_CORSO.MISTA_PRESENZA_TELEMATICA ? TIPO_CORSO.TELEMATICA_SINCRONA : classe?.corso?.modalita_erogazione
    });
    setIsEditMode(false);
    setEditingLessonId(null);
    setModalMode(MODAL_TYPES.NEW);
    setShowModal(true);
  };
  
  const handleSelectEvent = (event) => {
    const lesson = lessons.find(l => l.id_lezione === event.id_lezione);
    if (lesson) {
      setNewLesson({
        ...lesson,
        title: lesson.title,
        argomenti: lesson.argomenti?.map(arg => ({id_argomento: arg.id_argomento || arg, titolo: arg.titolo || arg})) || [],
        contenuti: lesson.contenuti?.map(cont => ({id_contenuto: cont.id_contenuto || cont, titolo: cont.titolo || cont})) || [],
        tipo_lezione: lesson.tipo_lezione || TIPO_CORSO.TELEMATICA_SINCRONA
      });
      setIsEditMode(true);
      setEditingLessonId(lesson.id_lezione);
      setModalMode(MODAL_TYPES.VIEW);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewLesson({});
  };

  const calculateTotalMinutes = () => {
    return lessons?.reduce((acc, lesson) => {
      const start = moment(`${lesson.data} ${lesson.ora_inizio}`);
      const end = moment(`${lesson.data} ${lesson.ora_fine}`);
      const duration = moment.duration(end.diff(start));
      return acc + duration.asMinutes();
    }, 0) || 0;
  };

  const formatHoursAndMinutes = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const calculateTotalHours = () => {
    const totalMinutes = calculateTotalMinutes();
    return formatHoursAndMinutes(totalMinutes);
  };

  const handleSaveLesson = async () => {
    if (!newLesson.titolo.trim()) {
      alertService.warn("Il titolo della lezione è obbligatorio", {autoClose: true});
      return;
    }

    if (newLesson.argomenti?.length === 0) {
      alertService.warn("Seleziona almeno un argomento per la lezione", {autoClose: true});
      return;
    }

    if (newLesson.ora_fine <= newLesson.ora_inizio) {
      alertService.warn("L'ora di fine deve essere successiva all'ora di inizio", {autoClose: true});
      return;
    }

    const start = moment(`${newLesson.data} ${newLesson.ora_inizio}`);
    const end = moment(`${newLesson.data} ${newLesson.ora_fine}`);
    const lessonMinutes = moment.duration(end.diff(start)).asMinutes();

    let totalProgrammedMinutes = calculateTotalMinutes();

    if (isEditMode) {
      const originalLesson = lessons.find(l => l.id_lezione === editingLessonId);
      if (originalLesson) {
        const originalStart = moment(`${originalLesson.data} ${originalLesson.ora_inizio}`);
        const originalEnd = moment(`${originalLesson.data} ${originalLesson.ora_fine}`);
        const originalMinutes = moment.duration(originalEnd.diff(originalStart)).asMinutes();
        totalProgrammedMinutes -= originalMinutes;
      }
    }

    const corsoDurataMinutes = Math.round(parseFloat(classe?.corso?.durata) * 60);

    if (Math.round(totalProgrammedMinutes + lessonMinutes) > corsoDurataMinutes) {
      const exceededMinutes = Math.round(totalProgrammedMinutes + lessonMinutes - corsoDurataMinutes);
      alertService.warn(`Hai superato le ore totali del corso (${classe?.corso?.durata} ore). Ore in eccesso: ${formatHoursAndMinutes(exceededMinutes)}.`, {autoClose: true});
      return;
    }

    const lessonData = {
      id_corso: classe?.corso?.id_corso,
      id_classe: classe?.id_classe,
      titolo: newLesson.titolo,
      data: newLesson.data,
      ora_inizio: newLesson.ora_inizio,
      tipo_lezione: classe?.corso?.modalita_erogazione === TIPO_CORSO.MISTA_PRESENZA_TELEMATICA ? newLesson.tipo_lezione : classe?.corso?.modalita_erogazione,
      ora_fine: newLesson.ora_fine,
      argomenti: newLesson.argomenti.map(arg => ({
        id_argomento: arg.id_argomento,
        titolo: arg.titolo
      })),
      contenuti: newLesson.contenuti.map(cont => ({
        id_contenuto: cont.id_contenuto,
        titolo: cont.titolo
      })),
      note: newLesson.note,
    };

    if (isEditMode) {
      lessonData.id_lezione = editingLessonId;
      const updatedLesson = await updateLezione(lessonData);
      setLessons(prevLessons => prevLessons.map(lesson => 
        lesson.id_lezione === editingLessonId ? formatLesson(updatedLesson) : lesson
      ));
    } else {
      const newLessonResponse = await createLezione(lessonData);
      setLessons(prevLessons => [...prevLessons, formatLesson(newLessonResponse)]);
    }

    handleCloseModal();
  };

  const formatLesson = (lesson) => ({
    ...lesson,
    data: lesson?.data?.split(' ')[0], 
  });

  const handleDeleteLesson = async () => {
    if (editingLessonId) {
      const obj = { id: editingLessonId }
      await deleteLezione(obj);
      setLessons(lessons.filter(lesson => lesson.id_lezione !== editingLessonId));
      handleCloseModal();
    }
  };

  const handleEditClick = () => {
    setModalMode(MODAL_TYPES.EDIT);
  };

  const handleDeleteClick = () => {
    setModalMode(MODAL_TYPES.DELETE);
  };

  const handleCancelDelete = () => {
    setModalMode(MODAL_TYPES.VIEW);
  };

  const searchArgomenti = async (params) => {
      const searchParams = {
        limite: 10,
        pagina: 1,
        filtri: {
          id_corso: classe?.corso?.id_corso,
          titolo: params.filtri?.titolo || ''
        },
        ordinamento: 'titolo asc'
      };

      const response = await getArgomentiByParams(searchParams);
      
      if (response && response.data) {
        return {
          data: response.data.map(argomento => ({
            id_argomento: argomento.id_argomento,
            titolo: argomento.titolo
          })),
          count: response.count || response.data.length
        };
      } else {
        return { data: [], count: 0 };
      }
  };

  const searchContenuti = async (params) => {
      const searchParams = {
        limite: 10,
        pagina: 1,
        filtri: {
          id_corso: classe?.corso?.id_corso,
          titolo: params.filtri?.titolo || ''
        },
        ordinamento: 'titolo ASC'
      };

      const response = await findContenutiByParams(searchParams);
      
      if (response && response.data) {
        return {
          data: response.data.map(contenuto => ({
            id_contenuto: contenuto.id_contenuto,
            titolo: contenuto.titolo
          })),
          count: response.count || response.data.length
        };
      } else {
        return { data: [], count: 0 };
      }
  };

  const renderModalBody = () => {
    switch (modalMode) {
      case MODAL_TYPES.NEW:
      case MODAL_TYPES.EDIT:
        return (
          <Form>
            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label><span className='text-muted'><b>Titolo</b></span></Form.Label>
                  <Form.Control
                    type="text"
                    value={newLesson.titolo || ''}
                    onChange={(e) => setNewLesson({ ...newLesson, titolo: e.target.value })}
                    placeholder="Inserisci il titolo della lezione"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                
                  <Row>
                    <Col xs={6}>
                    <Form.Label><span className='text-muted'><b>Orario inizio</b></span></Form.Label>
                      <Form.Control
                        type="time"
                        value={newLesson.ora_inizio || ''}
                        onChange={(e) => setNewLesson({ ...newLesson, ora_inizio: e.target.value })}
                      />
                    </Col>
                    <Col xs={6}>
                    <Form.Label><span className='text-muted'><b>Orario fine</b></span></Form.Label>
                      <Form.Control
                        type="time"
                        value={newLesson.ora_fine || ''}
                        onChange={(e) => setNewLesson({ ...newLesson, ora_fine: e.target.value })}
                      />
                    </Col>
                  </Row>
                </Form.Group>
              </Col>
            </Row>
                  {classe?.corso?.modalita_erogazione === TIPO_CORSO.MISTA_PRESENZA_TELEMATICA && (
                    <Row className="mb-3">
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label><span className='text-muted'><b>Tipo di lezione</b></span></Form.Label>
                          <div>
                            <Form.Check
                              type="radio"
                              label="Telematica sincrona"
                              name="tipo_lezione"
                              id={TIPO_CORSO.TELEMATICA_SINCRONA}
                              checked={newLesson.tipo_lezione  === TIPO_CORSO.TELEMATICA_SINCRONA}
                              onChange={() => setNewLesson({...newLesson, tipo_lezione: TIPO_CORSO.TELEMATICA_SINCRONA})}
                            />
                            <Form.Check
                              type="radio"
                              label="In presenza"
                              name="tipo_lezione"
                              id={TIPO_CORSO.PRESENZA}
                              checked={newLesson.tipo_lezione === TIPO_CORSO.PRESENZA}
                              onChange={() => setNewLesson({...newLesson, tipo_lezione: TIPO_CORSO.PRESENZA})}
                            />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <CustomSearch
                    setSelectedItem={(item) => {
                      if (item && !newLesson.argomenti?.some(arg => arg.id_argomento === item.id_argomento)) {
                        setNewLesson(prev => ({
                          ...prev,
                          argomenti: [...(prev.argomenti || []), item]
                        }));
                      }
                    }}
                    selectedItem={null}
                    searchApi={searchArgomenti}
                    field="titolo"
                    placeholder="Cerca un argomento..."
                    label="Cerca e aggiungi argomenti"
                  />
                  {newLesson.argomenti?.length > 0 && (
                    <div className="mt-2">
                      <h6>Argomenti selezionati:</h6>
                      <ul>
                        {newLesson.argomenti.map(arg => (
                          <li key={arg.id_argomento}>
                            {arg.titolo}
                            <Button
                              variant="link"
                              className="text-danger p-0 ms-2"
                              onClick={() => setNewLesson(prev => ({
                                ...prev,
                                argomenti: prev.argomenti?.filter(a => a.id_argomento !== arg.id_argomento) || []
                              }))}
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <CustomSearch
                    setSelectedItem={(item) => {
                      if (item && !newLesson.contenuti?.some(cont => cont.id_contenuto === item.id_contenuto)) {
                        setNewLesson(prev => ({
                          ...prev,
                          contenuti: [...(prev.contenuti || []), item]
                        }));
                      }
                    }}
                    selectedItem={null}
                    searchApi={searchContenuti}
                    field="titolo"
                    placeholder="Cerca un contenuto..."
                    label="Cerca e aggiungi contenuti"
                  />
                  {newLesson.contenuti?.length > 0 && (
                    <div className="mt-2">
                      <h6>Contenuti selezionati:</h6>
                      <ul>
                        {newLesson.contenuti.map(cont => (
                          <li key={cont.id_contenuto} style={{cursor: 'default'}}>
                             {UtilityService.generateIconContenuti(cont)}{' '}{cont?.titolo}
                            <Button
                              variant="link"
                              className="text-danger p-0 ms-2"
                              onClick={() => setNewLesson(prev => ({
                                ...prev,
                                contenuti: prev.contenuti?.filter(c => c.id_contenuto !== cont.id_contenuto) || []
                              }))}
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Note</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={newLesson.note || ''}
                    onChange={(e) => setNewLesson({ ...newLesson, note: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        );
      case MODAL_TYPES.VIEW:
        return (
          <div>
            <p><strong>Titolo:</strong> {newLesson.titolo}</p>
            <p><strong>Orario:</strong> {newLesson.ora_inizio?.split(':').slice(0, 2).join(':')} - {newLesson.ora_fine?.split(':').slice(0, 2).join(':')}</p>
            <p><strong>Modalità:</strong> {newLesson.tipo_lezione === TIPO_CORSO.TELEMATICA_SINCRONA ? "Telematica sincrona" : "Presenza"}</p>
            
          {
            newLesson.argomenti?.length > 0 && (
              <>
            <p><strong>Argomenti:</strong></p>
            <ul>
              {newLesson.argomenti?.map(arg => (
                <li key={typeof arg === 'object' ? arg.id_argomento : arg}>
                  {typeof arg === 'object' ? arg.titolo : `Argomento ID: ${arg}`}
                </li>
              ))}
            </ul>
              </>
             )
          }

            {
              newLesson.contenuti?.length > 0 && ( 
                <>
                <p><strong>Contenuti:</strong></p>
                <ul>
                  {newLesson.contenuti?.map(cont => (
                    <li key={typeof cont === 'object' ? cont.id_contenuto : cont}>
                      {typeof cont === 'object' ?          
                       <>
                        {UtilityService.generateIconContenuti(cont)}{' '}{cont?.titolo}
                      </> : `Contenuto ID: ${cont}`}
                    </li>
                  ))}
                </ul>
                </>
              )
            }
            {
              newLesson.note && (
                <p><strong>Note:</strong> {newLesson.note}</p>
              )
            }
            <Button onClick={handleEditClick} className="mx-1">Modifica</Button>
            <Button variant="danger" onClick={handleDeleteClick} className="mx-1">Elimina</Button>
          </div>
        );
      case MODAL_TYPES.DELETE:
        return (
          <div>
            <p>Sei sicuro di voler eliminare questa lezione?</p>
            <Button variant="danger" onClick={handleDeleteLesson} className="mx-1">
              {loadingStateLezioni.isLoadingLezioneDelete ? (<><Spinner animation="border" size="sm" />{" "}Caricamento...</>)  : "Conferma Eliminazione"}
            </Button>
            <Button variant="secondary" onClick={handleCancelDelete} className="mx-1">Annulla</Button>
          </div>
        );
        default:
          return null;
    }
  };

  if (!classe) {
    return <div>Caricamento...</div>;
  }

  return (
    <div className={`card custom-card w-100 shadow ${isWideScreen ? "p-5" : "px-1 py-3"}`} style={{ minHeight: "700px" }}>
      <div className="container">
        <ComebackArrow path={"/gestione-classi"} />
        <div className="d-flex justify-content-between custom-card-header mb-3">
          <div className="d-flex gap-3 custom-card-title my-2">
            <div className={`custom-icon-container`}>
              {classe?.corso?.immagine_corso ? 
                <img src={classe?.corso?.immagine_corso} className='custom-icon' alt='immagine corso'/> : 
                <i className={`bi custom-icon bi-calendar-event fs-2`}></i>}
            </div>
            <div className="align-self-center">
              <span className="fs-4 d-block">
                <i>
                  <b>
                    <h2>{classe?.corso?.denominazione}</h2>
                  </b>
                </i>
              </span>
            </div>
          </div>
          <div className="align-self-center my-2">
            <p>
              Ore totali: {classe?.corso?.durata} | Ore programmate: {calculateTotalHours()}
            </p>
          </div>
        </div>
        <div style={{ height: '100vh' }}>
          <CustomCalendar
            events={lessons.map(lesson => ({
              id_lezione: lesson.id_lezione,
              title: `${lesson.titolo}`,
              start: new Date(`${lesson.data}T${lesson.ora_inizio}`),
              end: new Date(`${lesson.data}T${lesson.ora_fine}`),
              allDay: false
            }))}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            minDate={minDate}
            maxDate={maxDate}
            isLoading={loadingStateLezioni.getLezioniByIdClasse}
          />
        </div>
        <CustomModal
          show={showModal}
          onHide={handleCloseModal}
          headerText={     
          <h5 className="form-control-static">
            {formatDate(newLesson.data)}
          </h5>}
          size={modalMode === MODAL_TYPES.NEW || modalMode === MODAL_TYPES.EDIT ? "lg" : "md"}
          btnText={modalMode === MODAL_TYPES.NEW || modalMode === MODAL_TYPES.EDIT ? (isEditMode ? "Aggiorna Lezione" : "Salva Lezione") : null}
          body={renderModalBody()}
          btnCancelText={'Chiudi'}
          closeButton={true}
          isLoading={loadingStateLezioni.isLoadingLezioneCreating || loadingStateLezioni.isLoadingLezioneUpdate}
          onUndoChanges={handleCloseModal}
          onSave={modalMode === MODAL_TYPES.NEW || modalMode === MODAL_TYPES.EDIT ? handleSaveLesson : null}
          hideButtons={modalMode !== MODAL_TYPES.NEW && modalMode !== MODAL_TYPES.EDIT}
        />
      </div>
    </div>
  );
}
